export type ZoneKind = 'paraphe' | 'signature';

export interface ParapheZone {
  id: string;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
  kind: ZoneKind;
}

export const ZONE_SIZE: Record<ZoneKind, { w: number; h: number }> = {
  paraphe: { w: 0.12, h: 0.045 },
  signature: { w: 0.22, h: 0.08 },
};

export const DEFAULT_ZONE_W = ZONE_SIZE.paraphe.w;
export const DEFAULT_ZONE_H = ZONE_SIZE.paraphe.h;

export function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

export function initialsFromName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/** Stamp for a paraphe box. Never uses the visa signature PNG. */
export function parapheStamp(
  fullName: string,
  parapheImageUrl: string | null | undefined,
  _signatureImageUrl?: string | null,
): { label: string; imageUrl: string | null } {
  const label = initialsFromName(fullName);
  const imageUrl = parapheImageUrl && parapheImageUrl.length > 0 ? parapheImageUrl : null;
  return { label, imageUrl };
}

export function boxStamp(
  kind: ZoneKind,
  fullName: string,
  parapheImageUrl: string | null | undefined,
  signatureImageUrl: string | null | undefined,
): { label: string; imageUrl: string | null } {
  if (kind === 'signature') {
    const imageUrl = signatureImageUrl && signatureImageUrl.length > 0 ? signatureImageUrl : null;
    return { label: fullName.trim() || '?', imageUrl };
  }
  return parapheStamp(fullName, parapheImageUrl, signatureImageUrl);
}

export function stampAssetPath(
  kind: ZoneKind,
  paraphePath: string | null | undefined,
  signaturePath: string | null | undefined,
): string | null {
  if (kind === 'signature') return signaturePath && signaturePath.length > 0 ? signaturePath : null;
  return paraphePath && paraphePath.length > 0 ? paraphePath : null;
}

export function parseZoneKind(raw: unknown): ZoneKind {
  return raw === 'signature' ? 'signature' : 'paraphe';
}

function placeWithSize(page: number, clickX: number, clickY: number, id: string, kind: ZoneKind, w: number, h: number): ParapheZone {
  const x = clamp01(clickX - w / 2);
  const y = clamp01(clickY - h / 2);
  return {
    id,
    page,
    kind,
    x: Math.min(x, 1 - w),
    y: Math.min(y, 1 - h),
    w,
    h,
  };
}

export function placeZoneAt(
  page: number,
  clickX: number,
  clickY: number,
  id: string,
  kind: ZoneKind = 'paraphe',
): ParapheZone {
  const { w, h } = ZONE_SIZE[kind];
  return placeWithSize(page, clickX, clickY, id, kind, w, h);
}

export function moveZone(zone: ParapheZone, x: number, y: number): ParapheZone {
  return {
    ...zone,
    x: Math.min(clamp01(x), 1 - zone.w),
    y: Math.min(clamp01(y), 1 - zone.h),
  };
}

export function setZoneKind(zone: ParapheZone, kind: ZoneKind): ParapheZone {
  if (zone.kind === kind) return zone;
  const { w, h } = ZONE_SIZE[kind];
  const cx = zone.x + zone.w / 2;
  const cy = zone.y + zone.h / 2;
  return placeWithSize(zone.page, cx, cy, zone.id, kind, w, h);
}

export function allBoxesFilled(zoneIds: string[], filledIds: string[]): boolean {
  if (zoneIds.length === 0) return false;
  const set = new Set(filledIds);
  return zoneIds.every((id) => set.has(id));
}

export function unfillOne(filledIds: string[], zoneId: string): string[] {
  return filledIds.filter((id) => id !== zoneId);
}

function isZoneShape(v: unknown): v is Record<string, unknown> {
  if (!v || typeof v !== 'object') return false;
  const z = v as Record<string, unknown>;
  return (
    typeof z.id === 'string' &&
    z.id.length > 0 &&
    typeof z.page === 'number' &&
    z.page >= 1 &&
    typeof z.x === 'number' &&
    typeof z.y === 'number' &&
    typeof z.w === 'number' &&
    typeof z.h === 'number'
  );
}

export function parseParapheZones(raw: unknown): ParapheZone[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(isZoneShape).map((z) => ({
    id: z.id as string,
    page: z.page as number,
    x: z.x as number,
    y: z.y as number,
    w: z.w as number,
    h: z.h as number,
    kind: parseZoneKind(z.kind),
  }));
}

export function parseFilledIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === 'string' && id.length > 0);
}
